import { COMMENT_TYPE } from "store/apis/notes";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { REPORT_TYPE } from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useHistory, useParams } from "react-router-dom";
import {
  useGetInteractiveReportQuery,
  useUpdateInteractiveReportMutation,
} from "store/apis/reports";
import ReportsAccordion from "./ReportsAccordion";
import { Account, InteractiveReport } from "types/Models/report";
import { ConditionalLink } from "components/conditionalLink";
import { Redirect } from "components/icons/Redirect";
import { ReportDownload } from "./InteractiveReport";
import dayjs from "dayjs";
import { DISPLAY_DATE, DD_MMM_YYYY_LT } from "constants/date";
import { Button } from "components/DesignSystem/Button/Button";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { ChangeEvent, useRef, useState } from "react";
import { debounce } from "utils/debouncing";
import Loader from "components/DesignSystem/Loader/loader";
import {
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  INCOME_STATEMENT,
  SAVED_REPORTS,
} from "constants/bookkeeping";
import { redirectToCategories } from "utils/redirectToCategories";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { ComparisonReport } from "./ComparisonReport";
import { ReportsType } from "pages/Books/Reports/Reports";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import * as Popover from "@radix-ui/react-popover";
import ThreeDots from "static/images/ThreeDots.svg";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { capitalize } from "utils/capitalize";
import { PERIOD } from "constants/revenueMetrics";
import { TableSelectionControls } from "components/GeneralLedger/TableSelectionControls";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

const DetailItem = ({
  title,
  value,
}: {
  title: string;
  value?: string | null;
}) => {
  return (
    <div className="t-flex t-justify-between t-w-full">
      <div className="t-text-body-sm t-text-text-30">{title}</div>
      <div className="t-text-body-sm">{value}</div>
    </div>
  );
};

export const ReportViewWithId = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const { reportId } = useParams<{ reportId: string }>();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const { alertToast } = useToast();
  const [selectedRows, setSelectedRows] = useState({});
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const { data, isLoading, isUninitialized } = useGetInteractiveReportQuery(
    { groupId, entityId, reportId },
    { skip: !groupId || !entityId || !reportId }
  );

  const {
    report_data,
    created_at,
    start_date,
    end_date,
    type,
    name,
    document_id,
    accounting_method = "CASH",
    comparison_report_type = "TOTALS",
    excel_document_id,
    show_non_zero_accounts,
  } = (data as InteractiveReport) || {};

  const onNewReport = () => {
    const reportTypeRoute = {
      [BALANCE_SHEET]: "balance-sheet",
      [INCOME_STATEMENT]: "income-statement",
      [CASH_FLOW_STATEMENT]: "cash-flow-statement",
      [SAVED_REPORTS]: "saved-reports",
    };

    if (!type) return;

    history.push(
      isCustomer
        ? `/books/live-financials/${reportTypeRoute[type]}?entity=${entityId}`
        : `/books/live-financials/${reportTypeRoute[type]}?company=${groupId}&entity=${entityId}`
    );
  };

  const reportBreadcrumbs = [
    {
      name: "Saved Report",
      link: isCustomer
        ? `/books/saved-reports?entity=${entityId}`
        : `/books/saved-reports?company=${groupId}&entity=${entityId}`,
    },
  ];

  const [updateReport] = useUpdateInteractiveReportMutation();

  const renameReport = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (name) {
      try {
        updateReport({
          groupId,
          entityId,
          reportId: reportId!,
          payload: {
            report_name: name,
          },
        }).unwrap();
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  });

  const portalContainer = useRef<HTMLDivElement>(null);

  if (isLoading || isUninitialized) {
    return <Loader />;
  }

  return (
    <DashboardLayout
      header={
        <Header
          v2
          left={
            <input
              onChange={renameReport}
              className="all:unset t-border t-border-l-0 t-border-r-0 t-border-t-0 t-border-dotted t-text-body-sm t-font-semibold md:t-text-[20px] md:t-font-bold t-w-1/2"
              defaultValue={
                REPORT_TYPE[data?.report_name || ""] || data?.report_name
              }
            />
          }
          breadcrumbs={reportBreadcrumbs}
        />
      }
    >
      <div
        className="t-grid t-grid-cols-3 t-w-full t-gap-7 t-h-full"
        ref={portalContainer}
      >
        <div className="t-col-span-2">
          {comparison_report_type === "TOTALS" ? (
            <>
              <ReportsAccordion.Root className="t-w-full t-shadow-[0px_1px_8px_-1px_#1F0C5C14] t-h-full t-overflow-y-auto t-rounded-lg t-boder t-border-neutral t-p-2 t-mt-1 t-pb-20">
                <ReportsAccordion.Trigger
                  data={report_data?.accounts}
                  filter={{
                    ACCOUNTING_METHOD: accounting_method!,
                    START_DATE: start_date!,
                    END_DATE: end_date!,
                    HIDE_ZERO: Boolean(show_non_zero_accounts),
                    VIEW_BY: comparison_report_type,
                    TXN_GROUP_ID: null,
                    OTHER: null,
                    CLASS_IDS: [],
                    CLASSIFICATION: [],
                    NO_CLASSIFICATION: [],
                    COMPARE_BY: null,
                    VARIANCE: [],
                    PERCENTAGE_MODE: null,
                    FORECAST_ENABLED: false,
                    FORECAST_START: "",
                    FORECAST_END: "",
                    FORECAST_SET_ID: null,
                  }}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  reportType={type}
                  secondaryAction={({ uuid }: Account) => (
                    <ConditionalLink
                      to={redirectToCategories({
                        uuid,
                        groupId,
                        entityId,
                        filter: {
                          ACCOUNTING_METHOD: accounting_method!,
                          START_DATE: start_date!,
                          END_DATE: end_date!,
                          HIDE_ZERO: Boolean(show_non_zero_accounts),
                          VIEW_BY: comparison_report_type,
                          CLASS_IDS: [],
                          TXN_GROUP_ID: null,
                          OTHER: null,
                          CLASSIFICATION: [],
                          NO_CLASSIFICATION: [],
                          COMPARE_BY: null,
                          VARIANCE: [],
                          PERCENTAGE_MODE: null,
                          FORECAST_ENABLED: false,
                          FORECAST_START: "",
                          FORECAST_END: "",
                          FORECAST_SET_ID: null,
                        },
                        isAdmin,
                      })}
                    >
                      <Redirect size={16} />
                    </ConditionalLink>
                  )}
                  primaryAction={({ uuid }: Account) => (
                    <AddCommentPopoverRoot contentId={uuid}>
                      <Dropdown.Root>
                        <Dropdown.Trigger>
                          <Popover.Anchor asChild>
                            <Button size="xsmall" customType="ghost_icon">
                              <img
                                src={ThreeDots}
                                alt="Action"
                                className="t-select-none"
                              />
                            </Button>
                          </Popover.Anchor>
                        </Dropdown.Trigger>
                        <Popover.Portal container={portalContainer.current}>
                          <AddCommentPop
                            commentType={
                              COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT
                            }
                            contentId={uuid}
                          />
                        </Popover.Portal>
                        <Dropdown.Content>
                          <Popover.Trigger asChild>
                            <Dropdown.Item>Add comment</Dropdown.Item>
                          </Popover.Trigger>
                        </Dropdown.Content>
                      </Dropdown.Root>
                    </AddCommentPopoverRoot>
                  )}
                />
              </ReportsAccordion.Root>
            </>
          ) : (
            <ComparisonReport
              showTxns={false}
              reportType={report_data?.report_type! as ReportsType}
              reportId={reportId}
              filter={{
                ACCOUNTING_METHOD: accounting_method!,
                START_DATE: start_date!,
                END_DATE: end_date!,
                HIDE_ZERO: Boolean(show_non_zero_accounts),
                VIEW_BY: comparison_report_type,
                TXN_GROUP_ID: null,
                OTHER: null,
                CLASS_IDS: [],
                CLASSIFICATION: [],
                NO_CLASSIFICATION: [],
                COMPARE_BY: null,
                VARIANCE: [],
                PERCENTAGE_MODE: null,
                FORECAST_ENABLED: false,
                FORECAST_START: "",
                FORECAST_END: "",
                FORECAST_SET_ID: null,
              }}
            />
          )}
        </div>

        <div className="t-flex t-col-span-1 t-flex-col t-gap-8 t-sticky md:t-top-0 t-h-min">
          <div className="t-flex t-flex-col t-gap-6">
            <div className="t-text-subtitle">Details</div>

            <div className="t-flex t-flex-col t-gap-2">
              <DetailItem
                title="From:"
                value={
                  start_date ? dayjs(start_date).format(DISPLAY_DATE) : "-"
                }
              />
              <DetailItem
                title="To:"
                value={end_date ? dayjs(end_date).format(DISPLAY_DATE) : "-"}
              />
              <DetailItem
                title="Accounting method:"
                value={capitalize(accounting_method!)}
              />
              <DetailItem
                title="Created On:"
                value={dayjs(created_at).format(DD_MMM_YYYY_LT)}
              />
              <DetailItem title="Created By:" value={name} />
            </div>
          </div>
          <div className="t-flex t-gap-2">
            <ReportDownload
              reportType={type!}
              isGroupByView={comparison_report_type !== PERIOD.TOTALS}
              documentId={document_id}
              excelDocumentId={excel_document_id}
              filter={{
                ACCOUNTING_METHOD: accounting_method!,
                START_DATE: start_date!,
                END_DATE: end_date!,
                HIDE_ZERO: Boolean(show_non_zero_accounts),
                VIEW_BY: comparison_report_type,
                CLASS_IDS: [],
                TXN_GROUP_ID: null,
                OTHER: null,
                CLASSIFICATION: [],
                NO_CLASSIFICATION: [],
                COMPARE_BY: null,
                VARIANCE: [],
                PERCENTAGE_MODE: null,
                FORECAST_ENABLED: false,
                FORECAST_START: "",
                FORECAST_END: "",
                FORECAST_SET_ID: null,
              }}
            />
            <Button size="xsmall" onClick={onNewReport}>
              New report
            </Button>
            {/* TODO: handle "Add to closing" with new Financial closing */}
            {/* {(isCpa || isForeignCA) && (
              <Button size="small" onClick={openAddToClosing}>
                Add to closing
              </Button>
            )} */}
          </div>
        </div>
      </div>
      <TransactionSlider />
      <TableSelectionControls
        deselectRows={() => {
          setSelectedRows?.({});
        }}
        allSelectedRows={selectedRows || {}}
        reportType={type}
        hasWritePermission={hasWritePermission}
      />
    </DashboardLayout>
  );
};
